import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";

import { OrganisationRoleConstants, OrganisationTypeConstants } from "../../../constants";
import { CursorChangeProps, ResultType } from "../../../models";
import { searchProjects, SearchProjectsResponse } from "../../../service/query";
import { ResultData } from "../../../service/Shared";
import { useAuth } from "../../../useAuth";
import { flattenObject } from "../../../utils";
import { getProjectDetailsByUuid } from "../../../utils/routes";
import { DataGridColumnDefinition, DataGridLinkCellFormatterData, dataGridMapFilterCriteria } from "../../../widget";

interface UseProjectsReturnData {
  columns: DataGridColumnDefinition[];
  isLoading: boolean;
  defaultSortingCriteria: SortColumn[];
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useProjects = (): UseProjectsReturnData => {
  const { currentUserRole } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const columns: DataGridColumnDefinition[] = [
    {
      name: "Project",
      key: "link",
      dataType: "string",
      formatter: "link",
    },
    {
      name: "Code",
      key: "standard.displayName",
      dataType: "string",
      minWidth: 200,
    },
    {
      name: "Project type",
      key: "projectType.displayName",
      dataType: "string",
    },
    {
      name: "Developer",
      key: "developer.displayName",
      dataType: "string",
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "projectStatusPill",
      alignment: "center",
      minWidth: 200,
    },
    {
      name: "Total units",
      key: "cachedUnitTotal",
      dataType: "string",
      filterable: false,
      minWidth: 112,
    },
    {
      name: "Available",
      key: "cachedUnitAvailableForSale",
      dataType: "string",
      filterable: false,
      minWidth: 112,
    },
    {
      name: "Reserved",
      key: "cachedUnitReserved",
      dataType: "string",
      filterable: false,
      minWidth: 112,
    },
    {
      name: "Sold",
      key: "cachedUnitSold",
      dataType: "string",
      filterable: false,
      minWidth: 112,
    },
  ];
  const pageSize = 10;

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "link", direction: "ASC" }];

  const formatData = useCallback(
    (responseData: SearchProjectsResponse | undefined): ResultData[] =>
      responseData?.results?.map((el) => {
        const result = flattenObject(el);

        result.link = <DataGridLinkCellFormatterData>{
          text: el.displayName,
          to:
            currentUserRole === OrganisationRoleConstants.VVB_MANAGER
              ? getProjectDetailsByUuid(el.uuid, OrganisationTypeConstants.VERIFIER)
              : undefined,
        };

        return result;
      }) || [],
    []
  );

  const onChange = async ({ filtering, paging, sorting }: CursorChangeProps): Promise<ResultType> => {
    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (filterCriteria.link) {
      filterCriteria.displayName = filterCriteria.link;
      filterCriteria.link = undefined;
    }

    let data: ResultType = {
      resultData: [],
      paging: {
        pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };
    await searchProjects({
      paging: {
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
        limit: pageSize,
      },
      /* eslint-disable @typescript-eslint/no-explicit-any */
      sort: sorting.map((s: { key: any; direction: any }) => ({
        key: s.key !== "link" ? s.key : "displayName",
        direction: s.direction,
      })),
      /* eslint-enable */
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setIsLoading(false);
      });

    return data;
  };

  return {
    columns,
    isLoading,
    defaultSortingCriteria,
    onChange,
  };
};
